/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareLinkedEntity } from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';

export const WRITER_NONE = "none";
export const WRITER_JAVASCRIPT = "javascript";
export const WRITER_JAVASCRIPT_DOWNLOAD = "javascript-download";
export const WRITER_NATIVE = "native";
export const WRITER_OTHER_CHANNELDEF = "other-channeldef";
export const WRITER_OTHER_SOURCEDEF = "other-sourcedef";

export const WRITER_TYPE = {
    [WRITER_NONE]: {
        name: "No writer"
    },
    [WRITER_JAVASCRIPT]: {
        name: "JavaScript"
    },
    [WRITER_JAVASCRIPT_DOWNLOAD]: {
        name: "JavaScript (only download)"
    },
    [WRITER_NATIVE]: {
        name: "Native (compiled-in)"
    },
    [WRITER_OTHER_CHANNELDEF]: {
        name: "From another application"
    },
    [WRITER_OTHER_SOURCEDEF]: {
        name: "Use from another source definition"
    },
};

export const CONVERSION_JS = "js";
export const CONVERSION_JSON = "json";
export const CONVERSION_XML = "xml";

export const CONVERSION_TYPE = {
    [CONVERSION_JS]: {
        name: "JavaScript Conversion"
    },
    [CONVERSION_JSON]: {
        name: "JSON",
    },
    [CONVERSION_XML]: {
        name: "XML",
    },
};

export const JAVASCRIPT_ENTRIES = {
    'options': 'options',
    'transmission': 'Transmission',
    'channel': 'Channel',
    'channelDef': 'ChannelDef',
    'logger': 'logger',
    'flow': 'Flow',
    'user': 'User',
    'stripHtml': 'stripHtml(value: string)',
    'executeDatiQuery': 'async executeDatiQuery(query: string, args: Map): Transmission',
    'Transmission': 'type Transmission (Transmission.FROM_JSON(), Transmission.EMPTY(), ...)',
    'getChannelOption': 'async getChannelOption(channel: Channel, optionKey: string): any',
    'setChannelOption': 'async setChannelOption(channel: Channel, optionKey: string, optionValue: any, ignoreChange: boolean)',
    'documentEnhancer': 'async documentEnhancer(transmission|row): Transmission',
    'doAction': 'doAction(args)',
    'doGraphql': 'doGraphql(args)',
    'getAuthOptions': 'getAuthOptions(channel: Channel, fetchId: string)',
    'setAuthOptions': 'setAuthOptions(channel: Channel, authOptions: Map)',
    'transmissionFromString': 'transmissionFromString(serializedTransmission: string): Transmission',
    'cleanupInvalidCharacters': 'cleanupInvalidCharacters(value: string): string',
    'getCountriesForLocale': 'getCountriesForLocale(locale : string) : object',
    'validateCzechBankAccount': 'validateCzechBankAccount(bankAccount: string) : boolean',
    'bankAccountToIBAN': 'bankAccountToIBAN({ bankCode : string, accountNumber : string, branchCode : string, countryCode = \'CZ\' }) : string',
    'TOTP': 'TOTP',
    'generateBarCodesToPDF': 'generateBarCodesToPDF({columns = 2, rows = 4, barcodeData = [], barCodeType = "code128"})',
    'memoryUsage': 'memoryUsage(): {rss: number, heapTotal: number, heapUsed: number, external: number}',
};

export const FLOW_JAVASCRIPT_ENTRIES = {
    ...JAVASCRIPT_ENTRIES,
    'deleteClipboard': 'async deleteClipboard: (key: string)',
    'getClipboard': 'async getClipboard: (key: string)',
    'getClipboardAsString': 'async getClipboardAsString: (key: string)',
    'setClipboard': 'async setClipboard: (key: string, content)',
    'getState': 'getState(): Map',
    'setState': 'setState(newState: Map)',
    'deleteData': 'deleteData(type: string, key: string): any',
    'deleteDataByValue': 'deleteDataByValue(type: string, value: Object): any',
    'listData': 'listData(type: string, filters, limit): any',
    'getData': 'getData(type: string, key: string): any',
    'getMultiData': 'getMultiData(type: string, keys: string[]): any',
    'setData': 'async setData(type: string, key: string, value: any)',

    'deleteChannelData': 'deleteChannelData(channel, type: string, key: string): any',
    'listChannelData': 'listChannelData(channel, type: string, filters, limit): any',
    'getChannelData': 'getChannelData(channel, type: string, key: string): any',
    'getChannelMultiData': 'getChannelMultiData(channel, type: string, keys: string[]): any',
    'setChannelData': 'async setChannelData(channel, type: string, key: string, value: any)',

    'markEmailAsProcessed': 'markEmailAsProcessed()',

    'lockMutex': 'async lockMutex(type: string, key: string, durationInSeconds: number)',
    'unlockMutex': 'async unlockMutex(type: string, key: string)',
    'matchErrors': 'async matchErrors(errorMessages)',
    'getTransmissionAfterStep': 'getTransmissionAfterStep(stepId: string)',
    'getOptionValue': 'getOptionValue(optionKey: string)',
    'setFlowOption': 'setFlowOption(optionKey: string, optionValue: any)',
    'getFlowOption': 'getFlowOption(optionKey: string): any',
    'finishFlow': 'async finishFlow()',
    'saveFlow': 'async saveFlow()',
    'continueFlow': 'async continueFlow(stepName: string)',
    'getChannelByAlias': 'async getChannelByAlias(channelAlias: string): Channel',
    'getChannelDefByAlias': 'async getChannelDefByAlias(channelAlias: string): ChannelDef',
    'hasChannelDef': 'hasChannelDef(keyOrChannelDefId: string, alias: string, noExtended?: boolean)',
    'setMaxProgress': 'async setMaxProgress(progress: integer)',
    'invokeFlowCoordinationEvent': 'async invokeFlowCoordinationEvent(coordinationDefId, args)',
    'invokeFlowCoordinationEventAndWait': 'async invokeFlowCoordinationEventAndWait(coordinationDefId, args)',
    'setProgress': 'async setProgress(progress: integer)',
    'getFlowTitle': 'async getFlowTitle() : string',
    //'getCurrentSelection': 'getCurrentSelection(): EntitySelection',
    'getServiceUrl': 'getServiceUrl(): string',
    'getServiceHookProxyUrl': 'getServiceHookProxyUrl(): string',
    'getFormUrl': 'getFormUrl(): string',
    'markTransferError': 'async markTransferError: ({channel, externalId, displayName, entityDefKey, errorMessages, fetchId})',
    'markTransferSuccess': 'async markTransferSuccess: ({channel, externalId, displayName, entityDefKey, fetchId})',
    'markFirstSuccessfulTransfer': 'async markFirstSuccessfulTransfer()',
    'getTransferErrorsForChannel': 'async getTransferErrorsForChannel: (alias, entityDefKey)',
    "convertReceivedData": "async convertReceivedData({channel, content, entityDef}) : Transmission",
    'notifyByEmail': 'async notifyByEmail (to: string[], subject: string, htmlBody: string)',
    'organisationName': 'organisationName : string',
    'organisationBillingName': 'organisationBillingName : string',
    'invocationContext': 'invocationContext : {}',
    'fetchIgnoredKeys': 'fetchIgnoredKeys(keys: string[], entityDefKey: string)',
    'filterIgnoredRows': 'filterIgnoredRows(rows: TransmissionRow[], entityDefKey: string)',
};

export const WRITER_JAVASCRIPT_ENTRIES = {
    ...JAVASCRIPT_ENTRIES,
    "json": "json",
    "parentJson": "parentJson",
    "writerOptions": "writerOptions",
    "row": "row",
    "entityDefKey": "entityDefKey",
    "findCountryCode": "findCountryCode(countryName: string, countryLang: string)",
    "addNewAdditionalItem": "addNewAdditionalItem(additionalObject: Object, entityDefKey: string, evidence: string)",
    "addNewAdditionalItemAfter": "addNewAdditionalItemAfter(additionalObject: Object, entityDefKey: string, evidence: string)",
    "getCachedKey": "getCachedKey(table: string, key: string) : any",
    "setCachedKey": "setCachedKey(table: string, key: string, value : any)",
    "stopWriter": "stopWriter()",
};

export default declareLinkedEntity({
    entity: "sourceDefs",
    graphQL: "SourceDef",
    name: "Source Definition",
    defaultOrder: ['name', 'type'],
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    parentRelation: 'sourceDefs',
    defaultTitle: (data) => {
        return data['name'];
    },
    dynamoPrefix: 'def',
    storageType: 'schema',
    onModify: ['channelDef'],
    fields: (types) => { return {
        id: {
            name: "Source Definition ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
            compositeKey: true,
        },
        channelDef: {
            name: "Application Definition",
            type: "relation",
            to: () => types['channelDefs'],
            defaultValue: (channelDef) => {
                return channelDef['id'];
            },
            required: true,
            parent: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        channelDefVersion: {
            name: "Channel Definition Version",
            to: () => types['channelDefVersions'],
            type: "relation",
            defaultValue: (channelDef) => {
                return channelDef['channelDefVersion_id'];
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        sourceGroupDef: {
            name: "Source Group",
            type: "relation",
            to: () => types['sourceGroupDefs'],
            relativeTo: ['channelDef'],
        },
        name: {
            name: "Name",
            trim: true,
            type: "string",
            translatable: true,
            defaultValue: "",
            required: true
        },
        order: {
            name: "Order",
            type: "int",
            defaultValue: 10,
        },
        pluralName: {
            name: "Plural Name",
            trim: true,
            translatable: true,
            type: "string",
            defaultValue: "",
        },
        dataType: {
            name: "Data type",
            type: "string",
            trim: true,
            description: "Name of data that will be used as part of URL/Parser/...",
            defaultValue: "",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        dataTypeForOne: {
            name: "Data type for One",
            type: "string",
            trim: true,
            description: "Name of data that will be used as part of URL/Pareser/... in case of single read operations (leave empty if same as Data Type)",
            defaultValue: "",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        urlFragment: {
            name: "URL Fragment",
            type: "string",
            trim: true,
            description: "URL Fragment that can be used in URL. Keep empty for default (Data type).",
            defaultValue: "",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        type: {
            name: "Type",
            type: "string",
            trim: true,
            description: "Internal name for better debugging in form of com.application.source",
            required: true,
            defaultValue: (channelDef) => {
                return channelDef['type'] + ".sourceDef";
            },
            validations: {
                matchRegexp: /^[a-zA-Z0-9\.\-]+$/,
            }
        },
        meaning: {
            name: "Common Meaning",
            type: "relation",
            to: () => types['entityDefs'],
            description: "Common meaning for this source. It is used for grouping same lists menus etc",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        filterOptions: {
            name: "Filter Options",
            type: "object",
            defaultValue: {},
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        pagerOptions: {
            name: "Pager Options",
            type: "object",
            defaultValue: {},
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        fetcherOptions: {
            name: "Fetcher Options",
            type: "object",
            defaultValue: {},
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        parserOptions: {
            name: "Parser Options",
            type: "object",
            defaultValue: {},
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        options: {
            name: "Options",
            type: "object",
            defaultValue: {},
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        ignoreFail: {
            name: "Ignore error",
            description: "This API Request can sometime fail and we should ignore it (licensing or permission issues)",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return data['disableImport'] !== true; },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        disableImport: {
            name: "Disable import",
            type: "boolean",
            description: "Enable this if data are not read in advance.",
            defaultValue: false,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        writerType: {
            name: "Has writer",
            type: "enum",
            description: "Enable if this data can be written.",
            defaultValue: WRITER_NONE,
            values: WRITER_TYPE,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        writerOptionsJavaScript: {
            name: "Writer Options JavaScript",
            type: "javascript",
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD; },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            globalVariables: {
                ...WRITER_JAVASCRIPT_ENTRIES,
            },
        },
        writerPrepareRowJavaScript: {
            name: "Writer Prepare JavaScript",
            type: "javascript",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD; },
            globalVariables: {
                ...WRITER_JAVASCRIPT_ENTRIES,
            },
        },
        writerRowJavaScript: {
            name: "Writer JavaScript (convert row to JSON)",
            type: "javascript",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD; },
            globalVariables: {
                ...WRITER_JAVASCRIPT_ENTRIES,
            },
        },
        writerConversion: {
            name: "Writer Conversion",
            type: "enum",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD; },
            values: CONVERSION_TYPE,
        },
        writerBeginString: {
            name: "Writer Begin String",
            type: "text",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return (data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD) && (data['writerConversion'] === CONVERSION_JSON || data['writerConversion'] === CONVERSION_XML); },
        },
        writerEndString: {
            name: "Writer End String",
            type: "text",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return (data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD) && (data['writerConversion'] === CONVERSION_JSON || data['writerConversion'] === CONVERSION_XML); },
        },
        writerEndJavaScript: {
            name: "Writer JavaScript (after last row)",
            type: "javascript",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return (data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD) && (data['writerConversion'] !== CONVERSION_JSON && data['writerConversion'] !== CONVERSION_XML); },
            globalVariables: {
                ...WRITER_JAVASCRIPT_ENTRIES,
                "content": "content: {}",
            },
        },
        writerProcessJavaScript: {
            name: "Writer JavaScript (perform operation)",
            type: "javascript",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT; },
            globalVariables: {
                ...WRITER_JAVASCRIPT_ENTRIES,
                "getItems": "getItems()",
                "setFetcherResult": "setFetcherResult(fetcherResult)",
                "setSuccessful": "setSuccessful(successful: boolean)",
                "setErrors": "setErrors(errors: Array)",
                "content": "content : string",
            },

        },
        writerValidationJavaScript: {
            name: "Writer Validation JavaScript",
            type: "javascript",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD; },
            globalVariables: {
                ...WRITER_JAVASCRIPT_ENTRIES,
                'writerResult': 'writerResult',
            }
        },
        writerPostProcessJavaScript: {
            name: "Writer JavaScript (post operation)",
            type: "javascript",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT; },
            globalVariables: {
                ...WRITER_JAVASCRIPT_ENTRIES,
                "getItems": "getItems()",
                "setFetcherResult": "setFetcherResult(fetcherResult)",
                "setSuccessful": "setSuccessful(successful: boolean)",
                "setErrors": "setErrors(errors: Array)",
                "result": "result",
            },
        },
        writerContentType: {
            name: "Writer Content Type",
            type: "string",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['writerType'] === WRITER_JAVASCRIPT || data['writerType'] === WRITER_JAVASCRIPT_DOWNLOAD; },
        },
        multiOperationSupported: {
            name: "Can process multiple rows at once",
            type: "boolean",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            description: "Can handle multiple rows in one file. Usually imports support multiple, REST API call support one",
            defaultValue: false,
        },
        disableImportStore: {
            name: "Disable Store of Import",
            type: "boolean",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            description: "Data can be read from backend but should not be stored.",
            defaultValue: false,
        },
        description: {
            name: "Description",
            type: "text",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            translatable: true,
            defaultValue: ""
        },
        disableFetchList: {
            name: "Disable list?",
            type: "boolean",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            defaultValue: false,
        },
        disableFetchOne: {
            name: "Disable get one row?",
            type: "boolean",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            defaultValue: false,
        },
        disableFetchPDF: {
            name: "Disable get PDF for row?",
            type: "boolean",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            defaultValue: true,
        },
        fetchDependency: {
            name: "Fetch dependency",
            type: "relation",
            to: () => types['sourceDefs'],
            relativeTo: ["channelDef"],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            description: "If API needs to make dependent requests."
        },
        swaggerId: {
            name: "Swagger ID",
            type: "string",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        apiDocUrl: {
            name: "API DOC URL",
            type: "string",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
    }; },
    relations: (types) => { return {
        channelEntityDefs: {
            type: 'handler',
            controllerFn: 'getAllChannelEntityDefs',
            to: types['channelEntityDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        entityDefs: {
            type: 'handler',
            controllerFn: 'getAllEntityDefs',
            to: types['entityDefs'],
        },
        dependentSourceDefs: {
            type: 'handler',
            controllerFn: 'getDependentSourceDefs',
            to: types['sourceDefs'],
        },
        operations: {
            type: 'oneToMany',
            to: types['sourceDefOperations'],
            export: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            defaultValue: () => {
                return {};
            },
        },

    }; }

});
